import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useAdminAccounts } from '../../hooks/useAdminAccounts';
import {
  getAccountRiskLabel,
  type AdminAccountDirectoryItem,
  type AdminAccountRiskFlag,
  type AdminAccountRole,
  type AdminAccountsMetric,
  type AdminAccountStatus,
  type AdminCapabilityState,
} from '../../services/adminAccountsService';

type RoleFilter = 'all' | AdminAccountRole;
type StatusFilter = 'all' | AdminAccountStatus;
type CapabilityFilter = 'all' | AdminCapabilityState;
type RiskFilter = 'all' | AdminAccountRiskFlag;
type SortOption =
  | 'updated_desc'
  | 'created_desc'
  | 'email_asc'
  | 'role_asc'
  | 'status_asc'
  | 'risk_desc';

const roleOrder: Record<AdminAccountRole, number> = {
  admin: 1,
  serviceAdmin: 2,
  client: 3,
  unknown: 4,
};

const statusOrder: Record<AdminAccountStatus, number> = {
  suspended: 1,
  pending_verification: 2,
  pending: 3,
  unknown: 4,
  active: 5,
};

const formatMetricValue = (metric: AdminAccountsMetric): string =>
  metric.value === null ? '—' : String(metric.value);

const Badge: React.FC<{
  children: React.ReactNode;
  tone?: 'default' | 'good' | 'warning' | 'danger' | 'neutral';
}> = ({ children, tone = 'default' }) => {
  const toneClass =
    tone === 'good'
      ? 'border-ratuj-300/30 bg-ratuj-300/10 text-ratuj-100'
      : tone === 'warning'
        ? 'border-amber-300/30 bg-amber-300/10 text-amber-100'
        : tone === 'danger'
          ? 'border-status-error-500/35 bg-status-error-500/10 text-status-error-300'
          : tone === 'neutral'
            ? 'border-primary-dark-300 bg-primary-dark-100 text-primary-light-400'
            : 'border-primary-dark-300/70 bg-primary-dark-200 text-primary-light-300';

  return (
    <span
      className={`inline-flex max-w-full items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${toneClass}`}
    >
      {children}
    </span>
  );
};

const MetricTile: React.FC<{
  metric: AdminAccountsMetric;
  tone?: 'good' | 'warning' | 'danger';
}> = ({ metric, tone }) => {
  const toneClass =
    tone === 'good'
      ? 'border-ratuj-300/30 bg-ratuj-300/10'
      : tone === 'warning'
        ? 'border-amber-300/30 bg-amber-300/10'
        : tone === 'danger'
          ? 'border-status-error-500/35 bg-status-error-500/10'
          : 'border-primary-dark-300/18 bg-primary-dark-100/10';
  return (
    <article className={`rounded-lg border p-3.5 ${toneClass}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-primary-light-300">{metric.label}</p>
        {metric.partial && (
          <span className="rounded-md border border-amber-300/25 px-1.5 py-0.5 text-[10px] font-medium text-amber-100">
            partial
          </span>
        )}
      </div>
      <p className="mt-2 text-2xl font-semibold text-primary-light-100">
        {formatMetricValue(metric)}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-primary-light-500">{metric.description}</p>
    </article>
  );
};

const getStatusTone = (
  status: AdminAccountStatus
): 'default' | 'good' | 'warning' | 'danger' | 'neutral' => {
  if (status === 'active') return 'good';
  if (status === 'suspended') return 'danger';
  if (status === 'pending' || status === 'pending_verification') return 'warning';
  return 'neutral';
};

const getRiskTone = (flag: AdminAccountRiskFlag): 'warning' | 'danger' | 'neutral' => {
  if (flag === 'suspended' || flag === 'unknownRole' || flag === 'unknownStatus') return 'danger';
  if (flag === 'adminIdentityWithoutAuthzVisibility') return 'neutral';
  return 'warning';
};

const detailValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Brak';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Brak';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const DetailField: React.FC<{ label: string; value: unknown; pre?: boolean }> = ({
  label,
  value,
  pre,
}) => (
  <div className="min-w-0">
    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
      {label}
    </p>
    {pre ? (
      <pre className="mt-1 max-h-40 overflow-auto rounded-md bg-primary-dark-100 p-2 text-xs text-primary-light-300">
        {detailValue(value)}
      </pre>
    ) : (
      <p className="mt-1 break-words text-sm text-primary-light-300">{detailValue(value)}</p>
    )}
  </div>
);

const AccountDetail: React.FC<{ account: AdminAccountDirectoryItem }> = ({ account }) => (
  <div className="mt-3 rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-3">
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
      <DetailField label="UID" value={account.uid} />
      <DetailField label="Email" value={account.email} />
      <DetailField label="Display name" value={account.displayName} />
      <DetailField label="Profil" value={account.profileCompletenessLabel} />
      <DetailField label="Rola" value={`${account.roleLabel} (${account.roleRaw || 'missing'})`} />
      <DetailField
        label="Status"
        value={`${account.statusLabel} (${account.statusRaw || 'missing'})`}
      />
      <DetailField label="Utworzono" value={account.createdAtLabel} />
      <DetailField label="Aktualizacja" value={account.updatedAtLabel} />
    </div>
    <div className="mt-3 grid grid-cols-1 gap-3 border-t border-primary-dark-300/55 pt-3 md:grid-cols-3">
      <DetailField label="Capability record" value={account.capabilityLabel} />
      <DetailField label="Capability note" value={account.capabilityDescription} />
      <DetailField
        label="Ryzyka"
        value={account.riskLabels.length ? account.riskLabels.join(', ') : 'Brak ryzyk'}
      />
    </div>
    <div className="mt-3 border-t border-primary-dark-300/55 pt-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
        Linked serviceProvider
      </p>
      {account.provider ? (
        <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-4">
          <DetailField label="Nazwa" value={account.provider.serviceName} />
          <DetailField label="Status" value={account.provider.verificationStatusLabel} />
          <DetailField label="isVerified" value={String(account.provider.isVerified)} />
          <DetailField label="Operacyjny" value={String(account.provider.isOperational)} />
        </div>
      ) : (
        <p className="mt-1 text-sm text-primary-light-400">Brak powiązanego profilu serwisu.</p>
      )}
    </div>
    <p className="mt-3 text-xs leading-relaxed text-primary-light-500">
      Actual custom claims innych użytkowników nie są widoczne w tym frontendowym read-modelu. Widok
      nie inferuje runtime dostępu admina z `users.role`.
    </p>
  </div>
);

const AccountRow: React.FC<{
  account: AdminAccountDirectoryItem;
  expanded: boolean;
  onToggle: () => void;
}> = ({ account, expanded, onToggle }) => (
  <article className="rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-3.5">
    <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="max-w-full break-words text-base font-semibold text-primary-light-100">
            {account.displayNameResolved}
          </h2>
          <Badge tone="default">{account.roleLabel}</Badge>
          <Badge tone={getStatusTone(account.status)}>{account.statusLabel}</Badge>
          <Badge tone="neutral">{account.capabilityLabel}</Badge>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-primary-light-500">
          <span className="break-all">{account.email || account.uid}</span>
          <span>Aktualizacja {account.updatedAtLabel}</span>
          {account.provider && <span>Serwis: {account.provider.serviceName}</span>}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {account.riskLabels.length ? (
            account.riskFlags.slice(0, 4).map((flag) => (
              <Badge key={flag} tone={getRiskTone(flag)}>
                {getAccountRiskLabel(flag)}
              </Badge>
            ))
          ) : (
            <Badge tone="good">Bez ryzyk na stronie</Badge>
          )}
          {account.riskLabels.length > 4 && (
            <Badge tone="neutral">+{account.riskLabels.length - 4}</Badge>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row xl:flex-shrink-0 xl:justify-end">
        {account.provider?.publicProfilePath && (
          <Link
            to={account.provider.publicProfilePath}
            className="inline-flex min-h-[2.25rem] items-center justify-center rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
          >
            Profil serwisu
          </Link>
        )}
        {account.role === 'serviceAdmin' && (
          <Link
            to="/admin/service-providers"
            className="inline-flex min-h-[2.25rem] items-center justify-center rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
          >
            Serwisy
          </Link>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex min-h-[2.25rem] items-center justify-center gap-1 rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
        >
          {expanded ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
          Szczegóły
        </button>
      </div>
    </div>
    {expanded && <AccountDetail account={account} />}
  </article>
);

const AdminAccountsPage: React.FC = () => {
  const { data, accounts, loading, loadingMore, error, refresh, loadMore } = useAdminAccounts();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [capabilityFilter, setCapabilityFilter] = useState<CapabilityFilter>('all');
  const [riskFilter, setRiskFilter] = useState<RiskFilter>('all');
  const [sortOption, setSortOption] = useState<SortOption>('updated_desc');
  const [expandedUid, setExpandedUid] = useState<string | null>(null);

  const filteredAccounts = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const filtered = accounts.filter((account) => {
      if (normalizedSearch && !account.searchText.includes(normalizedSearch)) return false;
      if (roleFilter !== 'all' && account.role !== roleFilter) return false;
      if (statusFilter !== 'all' && account.status !== statusFilter) return false;
      if (capabilityFilter !== 'all' && account.capabilityState !== capabilityFilter) return false;
      if (riskFilter !== 'all' && !account.riskFlags.includes(riskFilter)) return false;
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortOption === 'created_desc') {
        return (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0);
      }
      if (sortOption === 'email_asc') {
        return (a.email || a.uid).localeCompare(b.email || b.uid, 'pl');
      }
      if (sortOption === 'role_asc') {
        return (
          roleOrder[a.role] - roleOrder[b.role] ||
          a.displayNameResolved.localeCompare(b.displayNameResolved, 'pl')
        );
      }
      if (sortOption === 'status_asc') {
        return (
          statusOrder[a.status] - statusOrder[b.status] ||
          a.displayNameResolved.localeCompare(b.displayNameResolved, 'pl')
        );
      }
      if (sortOption === 'risk_desc') {
        return (
          b.riskFlags.length - a.riskFlags.length ||
          a.displayNameResolved.localeCompare(b.displayNameResolved, 'pl')
        );
      }
      return (b.updatedAt?.getTime() || 0) - (a.updatedAt?.getTime() || 0);
    });
  }, [accounts, capabilityFilter, riskFilter, roleFilter, search, sortOption, statusFilter]);

  if (loading && !data) {
    return (
      <section className="space-y-4" role="status" aria-label="Ładowanie monitora kont">
        <div className="rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-5">
          <div className="h-6 w-44 animate-pulse rounded bg-primary-dark-300" />
          <div className="mt-3 h-4 w-full max-w-xl animate-pulse rounded bg-primary-dark-300/75" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((item) => (
            <div
              key={item}
              className="h-28 animate-pulse rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75"
            />
          ))}
        </div>
      </section>
    );
  }

  if (error && !data) {
    return (
      <section className="rounded-lg border border-status-error-500/40 bg-status-error-500/10 p-5 text-status-error-200">
        <h1 className="text-base font-semibold">Monitor kont niedostępny</h1>
        <p className="mt-2 text-sm text-status-error-300">{error}</p>
        <button
          type="button"
          onClick={() => void refresh()}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-ratuj-400 px-3 py-2 text-sm font-medium text-white hover:bg-ratuj-300"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Ponów odczyt
        </button>
      </section>
    );
  }

  if (!data) return null;

  const metrics = data.metrics;

  return (
    <section className="space-y-4 sm:space-y-5">
      <header className="border-b border-primary-dark-300/18 px-1 pb-4 sm:px-0 sm:pb-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-primary-light-100 sm:text-2xl">
              Konta i dostęp
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-primary-light-300">
              Monitor ról, statusów kont i ryzyk capability.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-primary-light-400">
            <span className="rounded-lg border border-primary-dark-300 px-2.5 py-1.5">
              Odczyt: {data.readAt.toLocaleString('pl-PL')}
            </span>
            {data.isPartial && (
              <span className="rounded-lg border border-amber-300/30 bg-amber-300/10 px-2.5 py-1.5 text-amber-100">
                Dane częściowe
              </span>
            )}
            <button
              type="button"
              onClick={() => void refresh()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary-dark-300 px-2.5 py-1.5 text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Odśwież
            </button>
          </div>
        </div>
      </header>

      {data.partialError && (
        <div className="rounded-lg border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-amber-100">
          {data.partialError}
        </div>
      )}
      {error && data && (
        <div className="rounded-lg border border-status-error-500/40 bg-status-error-500/10 p-3 text-sm text-status-error-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile metric={metrics.total} />
        <MetricTile metric={metrics.clients} />
        <MetricTile metric={metrics.serviceAdmins} />
        <MetricTile metric={metrics.adminIdentities} />
        <MetricTile metric={metrics.active} tone="good" />
        <MetricTile metric={metrics.pending} tone="warning" />
        <MetricTile metric={metrics.suspended} tone="danger" />
        <MetricTile
          metric={metrics.withRisks}
          tone={(metrics.withRisks.value ?? 0) > 0 ? 'warning' : undefined}
        />
      </div>

      <section className="border-t border-primary-dark-300/16 px-1 py-4 sm:px-0 sm:py-5">
        <div className="mb-4 flex flex-col gap-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ratuj-200">
            Filtry
          </p>
          <h2 className="text-base font-semibold text-primary-light-100">Monitor kont</h2>
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-6">
          <label className="text-xs text-primary-light-400 lg:col-span-2">
            Szukaj
            <div className="relative mt-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-primary-light-500" />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Email, displayName, uid"
                className="w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 py-2 pl-9 text-sm text-primary-light-100 placeholder:text-primary-light-500"
              />
            </div>
          </label>
          <label className="text-xs text-primary-light-400">
            Rola
            <select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value as RoleFilter)}
              className="mt-1 w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 text-sm text-primary-light-100"
            >
              <option value="all">Wszystkie</option>
              <option value="client">Client</option>
              <option value="serviceAdmin">ServiceAdmin</option>
              <option value="admin">Admin</option>
              <option value="unknown">Unknown/legacy</option>
            </select>
          </label>
          <label className="text-xs text-primary-light-400">
            Status
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
              className="mt-1 w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 text-sm text-primary-light-100"
            >
              <option value="all">Wszystkie</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="pending_verification">Pending verification</option>
              <option value="suspended">Suspended</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>
          <label className="text-xs text-primary-light-400">
            Capability/authz
            <select
              value={capabilityFilter}
              onChange={(event) => setCapabilityFilter(event.target.value as CapabilityFilter)}
              className="mt-1 w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 text-sm text-primary-light-100"
            >
              <option value="all">Wszystkie</option>
              <option value="admin_authz_record">Admin authz record</option>
              <option value="service_admin_authz_record">ServiceAdmin authz record</option>
              <option value="no_authz_record">No authz record</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </label>
          <label className="text-xs text-primary-light-400">
            Sortowanie
            <select
              value={sortOption}
              onChange={(event) => setSortOption(event.target.value as SortOption)}
              className="mt-1 w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 text-sm text-primary-light-100"
            >
              <option value="updated_desc">Ostatnio aktualizowane</option>
              <option value="created_desc">Najnowsze</option>
              <option value="email_asc">Email A-Z</option>
              <option value="role_asc">Rola</option>
              <option value="status_asc">Status</option>
              <option value="risk_desc">Liczba ryzyk</option>
            </select>
          </label>
          <label className="text-xs text-primary-light-400 lg:col-span-2">
            Ryzyka
            <div className="relative mt-1">
              <FunnelIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-primary-light-500" />
              <select
                value={riskFilter}
                onChange={(event) => setRiskFilter(event.target.value as RiskFilter)}
                className="w-full rounded-lg border-primary-dark-300 bg-primary-dark-100 py-2 pl-9 text-sm text-primary-light-100"
              >
                <option value="all">Wszystkie</option>
                <option value="suspended">Suspended</option>
                <option value="serviceAdminWithoutProvider">
                  ServiceAdmin bez provider profile
                </option>
                <option value="providerWithoutUser">Provider bez user context</option>
                <option value="adminIdentityWithoutAuthzVisibility">
                  Admin identity bez authz visibility
                </option>
                <option value="incompleteProfile">Brak kompletnego profilu</option>
                <option value="unknownRole">Unknown role</option>
                <option value="unknownStatus">Unknown status</option>
              </select>
            </div>
          </label>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-primary-light-100">
              Wyniki ({filteredAccounts.length})
            </h2>
            <p className="mt-1 text-sm text-primary-light-500">
              Odczyt listy jest stronicowany po ID dokumentu `users`; filtry i sortowanie działają
              lokalnie na wczytanym zbiorze.
            </p>
          </div>
          {data.hasMore && (
            <span className="text-xs text-primary-light-500">
              Wczytano {accounts.length}; dostępne kolejne rekordy.
            </span>
          )}
        </div>

        {accounts.length === 0 ? (
          <div className="rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-5 text-sm text-primary-light-400">
            Brak dokumentów `users` w odczytanym monitorze.
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-5 text-sm text-primary-light-400">
            Brak wyników dla aktualnych filtrów. Zmień filtr albo wczytaj kolejną stronę kont.
          </div>
        ) : (
          <div className="space-y-2.5">
            {filteredAccounts.map((account) => (
              <AccountRow
                key={account.uid}
                account={account}
                expanded={expandedUid === account.uid}
                onToggle={() =>
                  setExpandedUid((current) => (current === account.uid ? null : account.uid))
                }
              />
            ))}
          </div>
        )}

        {data.orphanProviderPreviews.length > 0 && (
          <div className="rounded-lg border border-amber-300/30 bg-amber-300/10 p-3">
            <p className="text-sm font-semibold text-amber-100">
              Provider profiles bez user context w ograniczonym skanie
            </p>
            <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
              {data.orphanProviderPreviews.slice(0, 6).map((provider) => (
                <div key={provider.uid} className="rounded-md border border-amber-300/20 p-2">
                  <p className="break-words text-sm text-primary-light-100">
                    {provider.serviceName}
                  </p>
                  <p className="mt-1 break-all text-xs text-primary-light-500">
                    {provider.uid} · {provider.ownerEmail || 'brak emaila'} ·{' '}
                    {provider.verificationStatus}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-start gap-2 text-xs leading-relaxed text-primary-light-500">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-200" />
            Widok jest read-only: nie grantuje, nie revokuje, nie zawiesza i nie synchronizuje
            claimów.
          </p>
          {data.hasMore && (
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="inline-flex min-h-[2.5rem] items-center justify-center rounded-lg border border-primary-dark-300 px-4 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loadingMore ? 'Wczytywanie...' : 'Wczytaj więcej'}
            </button>
          )}
        </div>
      </section>
    </section>
  );
};

export default AdminAccountsPage;
